import { Core } from "@faire/design-tokens";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { IDocument } from "@faire/web-api--source/indigofair/data/IDocument";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React, { FC } from "react";

import { BYTES_IN_MB, DEFAULT_FILE_TYPES, MAX_DOCUMENT_MB_SIZE } from "./const";
import { DroppedFile, IDroppedFile } from "./DroppedFile";
import { EmptyDropzone } from "./EmptyDropzone";
import { extractName, useFileUploader } from "./useFileUploader";
import { useFileUploadErrorMessages } from "./useFileUploadErrorMessages";

export interface IFileUploaderProps {
  /**
   * State variable for documents that have been uploaded.
   */
  documents: IDocument[];
  /**
   * Callback to update the documents state.
   */
  setDocuments: (documents: IDocument[]) => void;
  /**
   * If uploaded file is an image, it also uploads an IImage for a preview. Use this to get the IImage.
   */
  onUploadImage?: (image: IImage) => void;
  /**
   * List of accepted file types.
   * Specify MIME types. See @link [MIME Docs](https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types).
   * Defaults to PNG, JPG and PDF.
   */
  acceptedFileTypes?: string[];
  /**
   * Localized message to communicate accepted file types to user.
   */
  acceptMessage?: string;
  /**
   * Max size of each file in MB. Defaults to 40 MB.
   */
  maxSize?: number;
  /**
   * Callback for when a file is successfully uploaded. Use this to track events.
   */
  onUploadSuccess?: (fileState: IDroppedFile) => void;
  /**
   * Callback for when a file fails to upload. Use this to track events.
   */
  onUploadFailure?: (fileState: IDroppedFile, errorMessage?: string) => void;
  /**
   * Hides upload count string.
   */
  hideUploadCount?: boolean;
  /**
   * Test ID for the nested dropzone is `${thisID}-Dropzone`.
   */
  "data-test-id"?: string;
}

/**
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/component/file-uploader/web/file-uploader)
 */
export const FileUploader: FC<IFileUploaderProps> = (props) => {
  const {
    documents,
    setDocuments,
    acceptMessage,
    onUploadImage,
    hideUploadCount,
    ...rest
  } = props;
  const acceptedFileTypes = props.acceptedFileTypes ?? DEFAULT_FILE_TYPES;
  const maxSize = (props.maxSize ?? MAX_DOCUMENT_MB_SIZE) * BYTES_IN_MB;

  const {
    uploading,
    onDropAccepted,
    onDropRejected,
    dragOver,
    setDragOver,
    onRemove,
    getPreviewImage,
  } = useFileUploader({
    documents,
    setDocuments,
    onUploadImage,
    onUploadSuccess: props.onUploadSuccess,
    onUploadFailure: props.onUploadFailure,
  });

  const localizedErrorMessage = useFileUploadErrorMessages({
    maxSize,
  });

  return (
    <Column>
      {/* Actual Dropzone */}
      <EmptyDropzone
        maxSize={maxSize}
        acceptedFileTypes={acceptedFileTypes}
        acceptMessage={acceptMessage}
        data-test-id={`${rest["data-test-id"] ?? "file-uploader"}-Dropzone`}
        onDropAccepted={onDropAccepted}
        onDropRejected={onDropRejected}
        dragOver={dragOver}
        setDragOver={setDragOver}
      />
      <Spacer height="1x" />
      {!hideUploadCount ? (
        <Typography color={Core.text.subdued} variant="labelSansRegular">
          <StrictLocalMsg
            defaultMessage="Uploaded: {currentNumber}"
            description={{
              text: "Message below a file drop box that tell users the number of files they have uploaded out of the max allowed number.",
              img: "https://cdn.faire.com/fastly/1bf88c9e178c41d1e46858cb64554ba34e8518a1d20724a7e9977ac723088cb5.png",
            }}
            values={{
              currentNumber: documents.length,
            }}
          />
        </Typography>
      ) : null}

      {/* Uploaded Files */}
      {documents.map(({ file_name }, index) => (
        <DroppedFile
          key={`${index}-${file_name}`}
          fileName={extractName(file_name) ?? ""}
          localizedErrorMessage={localizedErrorMessage}
          handleRemove={() => onRemove(file_name)}
          imgPreviewUrl={getPreviewImage(file_name)}
        />
      ))}

      {/* Uploading Files */}
      {Object.entries(uploading).map(([key, fileProps], index) => (
        <DroppedFile
          key={`${index}-${key}`}
          {...fileProps}
          localizedErrorMessage={localizedErrorMessage}
          handleRemove={() => onRemove(key)}
        />
      ))}
    </Column>
  );
};
